// client/main.tsx
// Main entry point for React application

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
// Auth imports (Sprint 10 - DC-890, DC-891, DC-618, DC-901)
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute, AdminRoute, PublicOnlyRoute } from './components/auth/ProtectedRoute';
import { Login } from './views/auth/Login';
import { Register } from './views/auth/Register';
import { ForgotPassword } from './views/auth/ForgotPassword';
import { ResetPassword } from './views/auth/ResetPassword';
import { AcceptInvitation } from './views/auth/AcceptInvitation';
import { Profile } from './views/auth/Profile';
import { EmailVerification } from './views/auth/EmailVerification';
import { AccessDenied } from './views/auth/AccessDenied';
import { OntologyViewer } from './views/OntologyViewer';
import { OntologyViewerV1 } from './views/OntologyViewerV1';
import { OntologyGraphViewer } from './views/OntologyGraphViewer';
import { FairCoreGridView } from './views/FairCoreGridView';
import { SemanticClarityView } from './views/SemanticClarityView';
import { EvaluatePage } from './views/EvaluatePage';
import { BulkEvaluatePage } from './views/BulkEvaluatePage';
import { OntologyMaturityPanel } from './components/OntologyMaturityPanel';
import { GraphSpecViewer } from './views/GraphSpecViewer';
import { OntologyStructureViewer } from './components/OntologyStructureViewer';
import OntologyStructureViewerAdvanced from './components/ontology-v1/OntologyStructureViewerAdvanced';
import HelpHintsPanel from './views/HelpHintsPanel';
import { JourneyDashboard } from './views/journey/JourneyDashboard';
import { JourneyNavigator } from './views/journey/JourneyNavigator';
import { TriangulationDashboard } from './views/triangulation/TriangulationDashboard';
import { DomainTriangulationView } from './views/triangulation/DomainTriangulationView';
import { ReadinessLayout } from './views/readiness/ReadinessLayout';
import { DatabricksImportPage } from './views/import/DatabricksImportPage';
import { NewAnalysisPage } from './views/import/NewAnalysisPage';
import { AppShell } from './components/shell/AppShell';
import { ProfileLanding } from './components/shell/ProfileLanding';
// DEPRECATED: JourneySummary - now integrated in JourneyNavigator Strategic tab
// import { JourneySummary } from './views/journey/JourneySummary';
import { HomeDashboard } from './views/Home';
import { RecommendationsView } from './views/journey/RecommendationsView';
import { AlcoaResultsView } from './views/journey/AlcoaResultsView';
import { ComplianceResultsView } from './views/journey/ComplianceResultsView';
import { FDACredibilityResultsView } from './views/journey/FDACredibilityResultsView';
import { BusinessValueCalculator } from './components/BusinessValueCalculator/BusinessValueCalculator';
import { InterviewResults } from './components/station-results/InterviewResults';
import { DataQualityScorecard } from './components/station-results/DataQualityScorecard';
import { MLReadinessResults } from './components/station-results/MLReadinessResults';
import { PrivacyResults } from './components/station-results/PrivacyResults';
import { RuleBrowser } from './views/fairByDesign/RuleBrowser';
import { Settings } from './views/settings/Settings';
import { OntologyManagement } from './views/settings/OntologyManagement';
import { TeamManagement } from './views/settings/TeamManagement';
import { TeamLayout } from './views/settings/TeamLayout';
import { UserManagement } from './views/settings/UserManagement';
import { AuditLogViewer } from './views/admin/AuditLogViewer';
import { SecurityDashboard } from './views/admin/SecurityDashboard';
import { ApiKeyManagement } from './views/admin/ApiKeyManagement';
import { SessionMonitor } from './views/admin/SessionMonitor';
import { Integrations } from './views/settings/Integrations';
import { GovernanceLayout } from './views/settings/GovernanceLayout';
import { DomainsAdmin } from './views/settings/DomainsAdmin';
import { FairPersonasAdmin } from './views/settings/FairPersonasAdmin';
import { GovernanceDashboard } from './views/settings/GovernanceDashboard';
import { OrganizationalInterviews } from './views/settings/OrganizationalInterviews';
import { GovernanceDocuments } from './views/settings/GovernanceDocuments';
import { CitationMappings } from './views/settings/CitationMappings';
import { GovernanceDocumentDetail } from './views/settings/GovernanceDocumentDetail';
import { GovernanceRecommendations } from './views/settings/GovernanceRecommendations';
import { InteroperabilityStandards } from './views/settings/InteroperabilityStandards';
import { EvidenceLibraryPage } from './views/settings/EvidenceLibraryPage';
import { AIPreferences } from './views/settings/AIPreferences';
import { PackAccuracy } from './views/settings/PackAccuracy';
import { OrganizationWorkflow } from './views/settings/OrganizationWorkflow';
import { OrgMaturityMatrix } from './views/maturity/OrgMaturityMatrix';
import { VendorDashboard } from './views/vendor/VendorDashboard';
import { VendorDetail } from './views/vendor/VendorDetail';
import { VendorForm } from './views/vendor/VendorForm';
import { AssessmentWizard } from './views/vendor/AssessmentWizard';
import { PortalLanding } from './views/vendor/portal/PortalLanding';
import { PortalUpload } from './views/vendor/portal/PortalUpload';
import { PortalComplete } from './views/vendor/portal/PortalComplete';
import { PortalReview } from './views/vendor/portal/PortalReview';
import { CertificationReview } from './views/vendor/CertificationReview';
import { ExpiryCalendar } from './views/vendor/reports/ExpiryCalendar';
import { VendorComparison } from './views/vendor/VendorComparison';
import { AssessmentHistory } from './views/vendor/AssessmentHistory';
import { AssessmentQueue } from './views/vendor/AssessmentQueue';
import { CertificationList } from './views/vendor/CertificationList';
import { StarterKitHub } from './views/StarterKitHub';
import SOPBrowser from './views/starter-kit/SOPBrowser';
import { PolicyTemplateBrowser } from './views/starter-kit/PolicyTemplateBrowser';
import { RACITemplateBrowser } from './views/starter-kit/RACITemplateBrowser';
import { UseCaseTemplateBrowser } from './views/starter-kit/UseCaseTemplateBrowser';
import PlaybookBrowser from './views/starter-kit/PlaybookBrowser';
// Banking AI use-case inventory (DC-1781) — banking_use_case_v1 surfaces.
import { BankingProfileRoute } from './views/banking/BankingProfileRoute';
import { BankingOverview } from './views/banking/BankingOverview';
import { UseCaseInventory } from './views/banking/UseCaseInventory';
import { UseCaseAdd } from './views/banking/UseCaseAdd';
import { UseCaseImport } from './views/banking/UseCaseImport';
import { UseCaseDetail } from './views/banking/UseCaseDetail';
import { ArcLibrary } from './views/banking/ArcLibrary';
import { TooltipProvider } from './components/ui/tooltip';
import { HelpOverridesProvider } from './state/HelpOverridesProvider';
import { HelpEditModeProvider } from './state/HelpEditMode';
import { ErrorBoundary } from './components/ErrorBoundary';
import LicenseBanner from './components/license/LicenseBanner';
import { useLicenseStatus } from './hooks/useLicenseStatus';
import './styles/compass-tokens.css';
import './styles/compass-theme.css';
import './styles/ontology.css';
import './styles/ontology-graph.css';
import './styles/ontology-management.css';
import './styles/journey.css';
import './styles/business-value-tokens.css';
import './styles/business-value-calculator.css';
import './styles/sentiment-dashboard.css';
import './styles/station-results.css';
import './styles/rule-browser.css';
import './styles/animations.css'; // DC-340: Global animation library
import './styles/auth.css'; // DC-903: Auth styles (Sprint 10)

// DQ Category Drawer standalone mount - exposes global functions for vanilla JS
// Sprint 13: Data Quality Category Explanation Drawers
import './components/dataQuality/DQCategoryDrawerMount';

// ML Readiness Metric Drawer standalone mount - exposes global functions for vanilla JS
// Sprint 13: ML Readiness Metric Explanation Drawers
import './components/mlReadiness/MLMetricDrawerMount';

// Inject the legacy ML Analysis card + modal scripts (public/js/mlAnalysis.js,
// public/js/mlAnalysisModal.js) at runtime as <script type="module">. They
// can't be referenced from index-vite.html because Vite's build resolver
// tries to bundle them; doing it here keeps them entirely outside Vite's
// graph while still loading before any Journey Navigator ML tab renders.
// mlAnalysis.js exposes window.MLAnalysis.renderMLAnalysisCard and
// self-installs the .ml-metric-info-btn → ml-metric-click click delegation.
// mlAnalysisModal.js exposes window.MLAnalysisModal.openMLAnalysisModal
// (used by the View Details button on the legacy card). DC-1549 redo.
function injectLegacyMLScript(id: string, src: string) {
  if (typeof document === 'undefined') return;
  if (document.getElementById(id)) return;
  const s = document.createElement('script');
  s.id = id;
  s.type = 'module';
  s.src = src;
  document.head.appendChild(s);
}
// ?v= matches the cache-buster on the index-vite.html legacy links — these
// URLs are unhashed, and a browser-cached pre-v2.0 copy would paint the old
// dark theme over the cream re-skin. Bump alongside index-vite.html.
injectLegacyMLScript('legacy-ml-analysis-script', '/js/mlAnalysis.js?v=2.0.0');
injectLegacyMLScript('legacy-ml-analysis-modal-script', '/js/mlAnalysisModal.js?v=2.0.0');

// The legacy modal renders into a fixed container by ID; create it once at
// startup so window.MLAnalysisModal.openMLAnalysisModal can find it later.
if (typeof document !== 'undefined' && !document.getElementById('mlAnalysisModal')) {
  const modalRoot = document.createElement('div');
  modalRoot.id = 'mlAnalysisModal';
  modalRoot.className = 'hidden';
  document.body.appendChild(modalRoot);
}

// DC-1648 follow-up: FAIR Personas moved from Settings → Governance to
// Settings → Team Management. Old bookmarks/deep-links to
// /settings/governance/fair-personas redirect with their query string
// preserved (?view=topology deep links keep working).
function FairPersonasRedirect() {
  const location = useLocation();
  return <Navigate to={`/settings/team/personas${location.search}`} replace />;
}

// DC-1645: Private Ontologies left Settings — the rail's Ontology destination
// is its only access point (operator decision 2026-06-11). The `settings-page`
// class is kept on the standalone wrapper because ontology-management.css
// descendants resolve the light-theme variable scope settings.css pins there
// (the DC-1602 invisible-panel lesson).
function OntologyHome() {
  return (
    <div className="settings-page">
      <OntologyManagement />
    </div>
  );
}

// Wrapper component to extract group_id from URL params and pass to BusinessValueCalculator
function BusinessValueCalculatorWrapper() {
  const { group_id } = useParams<{ group_id: string }>();

  if (!group_id) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Error: Missing Journey ID</h2>
        <p>Please navigate to this page from a journey.</p>
      </div>
    );
  }

  return <BusinessValueCalculator journeyId={group_id} />;
}

// Wrapper component for Interview Results
function InterviewResultsWrapper() {
  const { groupId } = useParams<{ groupId: string }>();
  const searchParams = new URLSearchParams(window.location.search);
  const jobId = searchParams.get('job_id');

  if (!groupId || !jobId) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Error: Missing Parameters</h2>
        <p>Journey ID: {groupId || 'Missing'}</p>
        <p>Job ID: {jobId || 'Missing'}</p>
      </div>
    );
  }

  return <InterviewResults jobId={jobId} journeyId={groupId} />;
}

// DEPRECATED: StrategicDashboardWrapper - Strategic Intelligence is now integrated in JourneyNavigator
// The /journey/:group_id/strategic route now redirects to /journey/:group_id

// Wrapper component for Data Quality Results (standalone page for vendor assessment "View Analysis")
function DataQualityResultsWrapper() {
  const { job_id } = useParams<{ job_id: string }>();
  const searchParams = new URLSearchParams(window.location.search);
  const jobIdFromQuery = searchParams.get('job_id') || job_id;

  if (!jobIdFromQuery) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text, #e8eefc)' }}>
        <h2>Error: Missing Job ID</h2>
        <p>Please access this page from an assessment or analysis.</p>
      </div>
    );
  }

  return (
    <div className="dq-results-page">
      <DataQualityScorecard jobId={jobIdFromQuery} />
    </div>
  );
}

// Wrapper component for ML Readiness Results (standalone page for vendor assessment "View Analysis")
function MLReadinessResultsWrapper() {
  const { job_id } = useParams<{ job_id: string }>();
  const searchParams = new URLSearchParams(window.location.search);
  const jobIdFromQuery = searchParams.get('job_id') || job_id;

  if (!jobIdFromQuery) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text, #e8eefc)' }}>
        <h2>Error: Missing Job ID</h2>
        <p>Please access this page from an assessment or analysis.</p>
      </div>
    );
  }

  return (
    <div className="dq-results-page">
      <MLReadinessResults jobId={jobIdFromQuery} />
    </div>
  );
}

// Wrapper component for Privacy Results (standalone page for Privacy station "View Analysis")
function PrivacyResultsWrapper() {
  const { job_id } = useParams<{ job_id: string }>();
  const searchParams = new URLSearchParams(window.location.search);
  const jobIdFromQuery = searchParams.get('job_id') || job_id;

  if (!jobIdFromQuery) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text, #e8eefc)' }}>
        <h2>Error: Missing Job ID</h2>
        <p>Please access this page from an assessment or analysis.</p>
      </div>
    );
  }

  return <PrivacyResults jobId={jobIdFromQuery} />;
}

function App() {
  const license = useLicenseStatus();

  return (
    <AuthProvider>
      <TooltipProvider delayDuration={300} skipDelayDuration={0}>
        <HelpOverridesProvider>
          <HelpEditModeProvider>
            <BrowserRouter>
              <LicenseBanner license={license} />
              <ErrorBoundary level="app" label="DATA Compass">
              {/* v2.0 One Compass shell — deep-teal left rail around every
                  authenticated surface; hides itself on auth/vendor routes. */}
              <AppShell>
              <Routes>
                {/* ============================================ */}
                {/* Auth Routes (Sprint 10 - DC-618, DC-892, DC-893) */}
                {/* ============================================ */}
                <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
                <Route path="/register" element={<PublicOnlyRoute><Register /></PublicOnlyRoute>} />
                <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />
                <Route path="/reset-password/:token" element={<PublicOnlyRoute><ResetPassword /></PublicOnlyRoute>} />
                <Route path="/accept-invitation/:token" element={<PublicOnlyRoute><AcceptInvitation /></PublicOnlyRoute>} />
                <Route path="/verify-email/:token" element={<EmailVerification />} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/access-denied" element={<AccessDenied />} />

                {/* ============================================ */}
                {/* Protected Analysis Routes (DC-901) */}
                {/* ============================================ */}
                {/* Legacy ontology viewer (column mappings) */}
                <Route path="/analysis/:job_id/ontology" element={<ProtectedRoute><OntologyViewer /></ProtectedRoute>} />

                {/* New v1.2 ontology viewer (OntologyBundle with FAIR Rules) */}
                <Route path="/analysis/:job_id/ontology-v1" element={<ProtectedRoute><OntologyViewerV1 /></ProtectedRoute>} />

                {/* Cytoscape-based FAIR graph viewer (v1.2 with interactive graph) */}
                <Route path="/analysis/:job_id/ontology-graph" element={<ProtectedRoute><OntologyGraphViewer /></ProtectedRoute>} />

                {/* Graph Spec v1.0 viewer (Neo4j-style graph explorer) */}
                <Route path="/analysis/:job_id/graph-spec" element={<ProtectedRoute><GraphSpecViewer /></ProtectedRoute>} />

                {/* v2.0 beat 3 — the FAIR Core grade grid is the FAIR surface;
                    the Cytoscape assessment graph is retired (no /graph route). */}
                <Route path="/analysis/:job_id/fair-core" element={<ProtectedRoute><FairCoreGridView /></ProtectedRoute>} />
                {/* v2.0 beat 6 — shallow Semantic Clarity summary; the deep
                    ontology explorer stays in the journey station. */}
                <Route path="/analysis/:job_id/semantic-clarity" element={<ProtectedRoute><SemanticClarityView /></ProtectedRoute>} />
                <Route path="/analysis/:job_id/evaluate" element={<ProtectedRoute><EvaluatePage /></ProtectedRoute>} />
                <Route path="/scope/:type/:id/evaluate" element={<ProtectedRoute><BulkEvaluatePage /></ProtectedRoute>} />
                <Route path="/analysis/:job_id/ontology-maturity" element={<ProtectedRoute><OntologyMaturityPanel /></ProtectedRoute>} />

                {/* Station Results Viewers for Vendor Assessment "View Analysis" buttons */}
                <Route path="/analysis/:job_id/data-quality" element={<ProtectedRoute><DataQualityResultsWrapper /></ProtectedRoute>} />
                <Route path="/analysis/:job_id/ml-readiness" element={<ProtectedRoute><MLReadinessResultsWrapper /></ProtectedRoute>} />
                <Route path="/analysis/:job_id/privacy" element={<ProtectedRoute><PrivacyResultsWrapper /></ProtectedRoute>} />

                {/* ============================================ */}
                {/* Protected Ontology & Utilities Routes (DC-901) */}
                {/* ============================================ */}
                {/* Standalone FAIR Ontology Structure viewer - Basic */}
                <Route path="/ontology/structure" element={<ProtectedRoute><OntologyStructureViewer /></ProtectedRoute>} />

                {/* Standalone FAIR Ontology Structure viewer - Advanced with progressive disclosure */}
                <Route path="/ontology/structure/advanced" element={<ProtectedRoute><OntologyStructureViewerAdvanced /></ProtectedRoute>} />

                {/* Utilities - Help & Hints management panel (Admin only) */}
                <Route path="/utilities/help-hints" element={<AdminRoute><HelpHintsPanel /></AdminRoute>} />

                {/* FAIR by Design - Validation Rule Browser (Sprint 1.1) */}
                <Route path="/fair-by-design/rules" element={<ProtectedRoute><RuleBrowser /></ProtectedRoute>} />

                {/* ============================================ */}
                {/* Protected Settings Routes (DC-901, regrouped DC-1645) */}
                {/* ============================================ */}
                {/* DC-1645: the IA is three rail buckets — People (/settings/team),
                    Governance (/settings/governance), Settings (/settings) — plus
                    Ontology (/ontology) as its own rail destination. People and
                    Governance render OUTSIDE the Settings chrome; URL prefixes are
                    kept so existing deep links survive via redirects. */}

                {/* Ontology — rail destination, the ONLY access point for Private
                    Ontologies (operator decision 2026-06-11; supersedes the design
                    doc's Decision 3 "move under Governance"). */}
                <Route path="/ontology" element={<ProtectedRoute><ErrorBoundary level="route" label="Ontology"><OntologyHome /></ErrorBoundary></ProtectedRoute>} />

                {/* People — Members | FAIR Personas | Interviews (DC-1648 + DC-1645) */}
                <Route path="/settings/team" element={<ProtectedRoute><ErrorBoundary level="route" label="People"><TeamLayout /></ErrorBoundary></ProtectedRoute>}>
                  <Route index element={<TeamManagement />} />
                  <Route path="personas" element={<FairPersonasAdmin />} />
                  <Route path="interviews" element={<OrganizationalInterviews />} />
                </Route>

                {/* Governance — tightened to governed evidence (DC-1645): Dashboard,
                    Documents, Standards, Recommendations, Evidence Library. Domains
                    and AI Preferences moved to Settings; Interviews moved to People. */}
                <Route path="/settings/governance" element={<ProtectedRoute><ErrorBoundary level="route" label="Governance"><GovernanceLayout /></ErrorBoundary></ProtectedRoute>}>
                  {/* DC-1646: the Execution Risk dashboard is a readiness readout,
                      not governed evidence — its canonical home is now
                      Readiness → Organizational. Governance lands on Documents. */}
                  <Route index element={<Navigate to="/settings/governance/documents" replace />} />
                  <Route path="dashboard" element={<Navigate to="/readiness/organizational" replace />} />
                  <Route path="domains" element={<Navigate to="/settings/domains" replace />} />
                  {/* DC-1648: the legacy standalone "Personas" topology page is retired —
                      FAIR personas are the only persona system, and the surface lives
                      under People (Kathleen 6/10: "one place for team + persona"). */}
                  <Route
                    path="personas"
                    element={<Navigate to="/settings/team/personas?view=topology" replace />}
                  />
                  <Route path="fair-personas" element={<FairPersonasRedirect />} />
                  <Route path="interviews" element={<Navigate to="/settings/team/interviews" replace />} />
                  <Route path="documents" element={<GovernanceDocuments />} />
                  <Route path="documents/:documentId" element={<GovernanceDocumentDetail />} />
                  {/* DC-1669: internal governance citation review (approval gate) */}
                  <Route path="citation-mappings" element={<CitationMappings />} />
                  <Route path="standards" element={<InteroperabilityStandards />} />
                  <Route path="recommendations" element={<GovernanceRecommendations />} />
                  <Route path="evidence-library" element={<EvidenceLibraryPage />} />
                  <Route path="ai-preferences" element={<Navigate to="/settings/ai-preferences" replace />} />
                </Route>

                {/* Settings — true configuration + cross-cutting primitives (DC-1645):
                    Domains, Integrations, AI Preferences + the Access & Security
                    admin cluster. */}
                <Route path="/settings" element={<ProtectedRoute><ErrorBoundary level="route" label="Settings"><Settings /></ErrorBoundary></ProtectedRoute>}>
                  <Route path="ontologies" element={<Navigate to="/ontology" replace />} />
                  {/* Organization Workflow — org-wide starting point, admin-only
                      (CC-V35-BANK-DEMO-READY-01) */}
                  <Route path="workflow" element={<OrganizationWorkflow />} />
                  {/* Domains — cross-cutting scope primitive, moved from Governance (DC-1645) */}
                  <Route path="domains" element={<DomainsAdmin />} />
                  {/* AI Preferences — config, moved from Governance (DC-1645) */}
                  <Route path="ai-preferences" element={<AIPreferences />} />
                  {/* Pack accuracy — Domain Pack override telemetry, Admin Only (DC-1517) */}
                  <Route path="pack-accuracy" element={<PackAccuracy />} />
                  {/* User Management - Admin Only (DC-627) */}
                  <Route path="users" element={<UserManagement />} />
                  {/* Audit Log Viewer - Admin Only (DC-636) */}
                  <Route path="audit-logs" element={<AuditLogViewer />} />
                  {/* Security Dashboard - Admin Only (DC-637) */}
                  <Route path="security" element={<SecurityDashboard />} />
                  {/* API Key Management - Admin Only (DC-629) */}
                  <Route path="api-keys" element={<ApiKeyManagement />} />
                  {/* Session Monitor - Admin Only (DC-630) */}
                  <Route path="sessions" element={<SessionMonitor />} />
                  {/* Integrations - Admin Only (DC-1157) */}
                  <Route path="integrations" element={<Integrations />} />
                </Route>

                {/* ============================================ */}
                {/* Protected Home & Vendor Routes (DC-901) */}
                {/* ============================================ */}
                {/* Home Dashboard - New Entry Point (Sprint 9) */}
                <Route path="/home" element={<ProtectedRoute><ErrorBoundary level="route" label="Home Dashboard"><HomeDashboard /></ErrorBoundary></ProtectedRoute>} />

                {/* Starter Kit Hub (Sprint 15) */}
                <Route path="/starter-kit" element={
                  <ProtectedRoute>
                    <ErrorBoundary level="route" label="Starter Kit">
                      <StarterKitHub />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/starter-kit/sops" element={<ProtectedRoute><SOPBrowser /></ProtectedRoute>} />
                <Route path="/starter-kit/policy-templates" element={<ProtectedRoute><PolicyTemplateBrowser /></ProtectedRoute>} />
                <Route path="/starter-kit/raci-templates" element={<ProtectedRoute><RACITemplateBrowser /></ProtectedRoute>} />
                <Route path="/starter-kit/use-cases" element={<ProtectedRoute><UseCaseTemplateBrowser /></ProtectedRoute>} />
                <Route path="/starter-kit/playbooks" element={<ProtectedRoute><PlaybookBrowser /></ProtectedRoute>} />

                {/* Vendor Certification Program (Sprint 9 - DC-523) */}
                <Route path="/vendors" element={<ProtectedRoute><ErrorBoundary level="route" label="Vendor Dashboard"><VendorDashboard /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/vendors/new" element={<ProtectedRoute><VendorForm /></ProtectedRoute>} />
                <Route path="/vendors/:vendorId" element={<ProtectedRoute><VendorDetail /></ProtectedRoute>} />
                <Route path="/vendors/:vendorId/edit" element={<ProtectedRoute><VendorForm /></ProtectedRoute>} />
                {/* Assessment routes - with product selection */}
                <Route path="/vendors/:vendorId/assess" element={<ProtectedRoute><VendorDetail startAssessment={true} /></ProtectedRoute>} />
                <Route path="/vendors/:vendorId/products/:productId" element={<ProtectedRoute><VendorDetail /></ProtectedRoute>} />
                <Route path="/vendors/:vendorId/products/:productId/assess" element={<ProtectedRoute><AssessmentWizard /></ProtectedRoute>} />
                <Route path="/vendors/:vendorId/products/:productId/assess/:assessmentId" element={<ProtectedRoute><AssessmentWizard /></ProtectedRoute>} />
                <Route path="/vendors/:vendorId/assessments/:assessmentId" element={<ProtectedRoute><AssessmentWizard /></ProtectedRoute>} />

                {/* Vendor Portal - External Token-Based Access (DC-532) - NOT protected (public portal) */}
                <Route path="/portal/:token" element={<PortalLanding />} />
                <Route path="/portal/:token/upload" element={<PortalUpload />} />
                <Route path="/portal/:token/review" element={<PortalReview />} />
                <Route path="/portal/:token/complete" element={<PortalComplete />} />

                {/* Assessment Queue - Auditor Queue View (DC-523 Remediation) */}
                <Route path="/assessments/queue" element={<ProtectedRoute><AssessmentQueue /></ProtectedRoute>} />

                {/* Certification Review - Auditor Interface (DC-554) */}
                <Route path="/assessments/:assessmentId/review" element={<ProtectedRoute><CertificationReview /></ProtectedRoute>} />

                {/* Certifications List - Phase 9-10 */}
                <Route path="/certifications" element={<ProtectedRoute><CertificationList /></ProtectedRoute>} />

                {/* Vendor Reports - Phase 12 */}
                <Route path="/vendors/reports/expiry-calendar" element={<ProtectedRoute><ExpiryCalendar /></ProtectedRoute>} />
                <Route path="/vendors/reports/comparison" element={<ProtectedRoute><VendorComparison /></ProtectedRoute>} />
                <Route path="/vendors/products/:productId/history" element={<ProtectedRoute><AssessmentHistory /></ProtectedRoute>} />

                {/* ============================================ */}
                {/* Protected Journey Routes (DC-901) */}
                {/* ============================================ */}
                {/* Journey Feature - Data Maturity Journeys */}
                <Route path="/journeys" element={<ProtectedRoute><ErrorBoundary level="route" label="Journey Dashboard"><JourneyDashboard /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/journey" element={<ProtectedRoute><ErrorBoundary level="route" label="Journey Dashboard"><JourneyDashboard /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/journey/new" element={<ProtectedRoute><ErrorBoundary level="route" label="Journey Dashboard"><JourneyDashboard autoOpenCreate={true} /></ErrorBoundary></ProtectedRoute>} />
                <Route path="/journey/:group_id" element={<ProtectedRoute><ErrorBoundary level="route" label="Journey Navigator"><JourneyNavigator /></ErrorBoundary></ProtectedRoute>} />
                {/* DEPRECATED: Summary and Strategic routes - now integrated in JourneyNavigator */}
                <Route path="/journey/:group_id/summary" element={<Navigate to=".." replace />} />
                <Route path="/journey/:group_id/strategic" element={<Navigate to=".." replace />} />
                <Route path="/journey/:group_id/recommendations" element={<ProtectedRoute><RecommendationsView /></ProtectedRoute>} />
                {/* ALCOA+ Regulatory Compliance Results (DC-520) */}
                <Route path="/journey/:group_id/alcoa" element={<ProtectedRoute><AlcoaResultsView /></ProtectedRoute>} />
                {/* Privacy Compliance Results (DC-821) */}
                <Route path="/journey/:group_id/compliance" element={<ProtectedRoute><ComplianceResultsView /></ProtectedRoute>} />
                {/* FDA AI Credibility Results (DC-934) */}
                <Route path="/journey/:group_id/fda-credibility" element={<ProtectedRoute><FDACredibilityResultsView /></ProtectedRoute>} />
                <Route
                  path="/journey/:groupId/station/interviews/results"
                  element={<ProtectedRoute><InterviewResultsWrapper /></ProtectedRoute>}
                />
                <Route path="/journey/:group_id/station/:station_id/results" element={<ProtectedRoute><JourneyNavigator /></ProtectedRoute>} />
                <Route
                  path="/journey/:group_id/calculator"
                  element={<ProtectedRoute><BusinessValueCalculatorWrapper /></ProtectedRoute>}
                />

                {/* ============================================ */}
                {/* New analysis — source-agnostic entry (file OR Databricks) */}
                {/* ============================================ */}
                <Route
                  path="/import"
                  element={
                    <ProtectedRoute>
                      <ErrorBoundary level="route" label="New Analysis">
                        <NewAnalysisPage />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  }
                />

                {/* Databricks Import — full page (v2.0 beat 1)  */}
                {/* ============================================ */}
                <Route
                  path="/import/databricks"
                  element={
                    <ProtectedRoute>
                      <ErrorBoundary level="route" label="Databricks Import">
                        <DatabricksImportPage />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  }
                />

                {/* ============================================ */}
                {/* Readiness — two lenses, one zoom (DC-1646)    */}
                {/* ============================================ */}
                {/* The consolidated Readiness destination: Data Readiness (the
                    Triangulation domain list = the Data lens at domain zoom) |
                    Organizational Readiness (the Execution Risk dashboard's one
                    canonical home — moved out of Governance). The Data lens's
                    Org zoom (cross-dataset rollup, DC-1625) stays parked. */}
                <Route path="/readiness" element={<ProtectedRoute><ErrorBoundary level="route" label="Readiness"><ReadinessLayout /></ErrorBoundary></ProtectedRoute>}>
                  <Route index element={<TriangulationDashboard />} />
                  <Route path="organizational" element={<GovernanceDashboard />} />
                  {/* DC-1613 2d — the org-capability axis (7×6 FAIR-MM projection). */}
                  <Route path="maturity" element={<OrgMaturityMatrix />} />
                </Route>
                <Route path="/triangulation" element={<Navigate to="/readiness" replace />} />
                {/* Domain zoom of the Data lens — URL unchanged (demo path). */}
                <Route
                  path="/triangulation/:domainId"
                  element={
                    <ProtectedRoute>
                      <ErrorBoundary level="route" label="Domain Triangulation">
                        <DomainTriangulationView />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  }
                />

                {/* Banking Overview (D-BANK-UX-03) — the profile's landing
                    surface: the defensibility chain, the organization's own
                    position on it, and the one decision its state says it
                    owes. Same presentation-layer gate as every banking route. */}
                <Route path="/overview" element={<ProtectedRoute><BankingProfileRoute><ErrorBoundary level="route" label="Overview"><BankingOverview /></ErrorBoundary></BankingProfileRoute></ProtectedRoute>} />

                {/* Banking AI use-case inventory (DC-1781). Profile-gated at
                    the PRESENTATION layer only — every /api/use-cases call is
                    server-authorized independently (accepted design §5.1).
                    Order: /new and /import before /:id so neither is
                    shadowed by the parameter route. */}
                <Route path="/use-cases" element={<ProtectedRoute><BankingProfileRoute><ErrorBoundary level="route" label="AI Use Cases"><UseCaseInventory /></ErrorBoundary></BankingProfileRoute></ProtectedRoute>} />
                <Route path="/use-cases/new" element={<ProtectedRoute><BankingProfileRoute><UseCaseAdd /></BankingProfileRoute></ProtectedRoute>} />
                <Route path="/use-cases/import" element={<ProtectedRoute><BankingProfileRoute><UseCaseImport /></BankingProfileRoute></ProtectedRoute>} />
                <Route path="/use-cases/:id" element={<ProtectedRoute><BankingProfileRoute><UseCaseDetail /></BankingProfileRoute></ProtectedRoute>} />
                {/* ARC Library (DC-1735): the entitled, versioned catalog browser. */}
                <Route path="/arc-library" element={<ProtectedRoute><BankingProfileRoute><ErrorBoundary level="route" label="ARC Library"><ArcLibrary /></ErrorBoundary></BankingProfileRoute></ProtectedRoute>} />

                {/* Default route — resolved from the organization's
                    experience profile (CC-V35-BANK-PROFILE-01, design §5.6).
                    core_dataset organizations land on /home exactly as
                    before; the redirect target comes from the code registry,
                    never from the database or localStorage.

                    ProtectedRoute is REQUIRED here, and this was the one route
                    in the file missing it. ProfileLanding asks "which
                    workspace?", a question fed by the /api/iam/me bootstrap.
                    Signed out that call 401s, the profile never leaves
                    'pending', and the pending state is deliberately an empty
                    aria-busy div — so an unauthenticated visitor to the site
                    root got a permanently blank page instead of a login form.
                    Establish WHICH USER before asking which workspace. */}
                <Route path="/" element={<ProtectedRoute><ProfileLanding /></ProtectedRoute>} />
              </Routes>
              </AppShell>
              </ErrorBoundary>
            </BrowserRouter>
          </HelpEditModeProvider>
        </HelpOverridesProvider>
      </TooltipProvider>
    </AuthProvider>
  );
}

const root = document.getElementById('root');
if (root) {
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}
